import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = {
  // Article ingestion
  ingestArticle: async (data) => {
    try {
      const response = await axios.post(`${API}/articles/ingest`, data);
      return response.data;
    } catch (error) {
      if (error.response?.status === 409) {
        throw new Error('This article has already been added to the platform');
      }
      throw error;
    }
  },

  // Score endpoints
  getImpactByDOI: async (doi) => {
    const response = await axios.get(`${API}/impact/${encodeURIComponent(doi)}`);
    return response.data;
  },

  recalculateImpact: async (doi) => {
    const response = await axios.post(`${API}/recalculate-impact/${encodeURIComponent(doi)}`);
    return response.data;
  },

  // Admin endpoints
  getAllArticles: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.study_type && filters.study_type !== 'all') params.append('study_type', filters.study_type);
    if (filters.evidence_level && filters.evidence_level !== 'all') params.append('evidence_level', filters.evidence_level);
    if (filters.limit) params.append('limit', filters.limit);
    
    const response = await axios.get(`${API}/admin/articles?${params}`);
    return response.data;
  },

  getArticleDetail: async (articleId) => {
    const response = await axios.get(`${API}/admin/article/${articleId}`);
    return response.data;
  },

  deleteArticle: async (articleId) => {
    const response = await axios.delete(`${API}/admin/article/${articleId}`);
    return response.data;
  },

  updateVisibilityWeights: async (weights) => {
    const response = await axios.put(`${API}/admin/visibility-weights`, weights);
    return response.data;
  },

  getVisibilityWeights: async () => {
    const response = await axios.get(`${API}/admin/visibility-weights`);
    return response.data;
  },

  // Public explorer endpoints
  exploreArticles: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.study_type && filters.study_type !== 'all') params.append('study_type', filters.study_type);
    if (filters.evidence_level && filters.evidence_level !== 'all') params.append('evidence_level', filters.evidence_level);
    if (filters.min_year && filters.min_year !== 'all') params.append('min_year', filters.min_year);
    if (filters.limit) params.append('limit', filters.limit);
    
    // Add cache busting
    params.append('_t', Date.now());
    
    const response = await axios.get(`${API}/explorer/articles?${params}`);
    return response.data;
  },

  getTopImpact: async (limit = 10) => {
    const response = await axios.get(`${API}/explorer/top-impact?limit=${limit}`);
    return response.data;
  },

  getMostRigorous: async (limit = 10) => {
    const response = await axios.get(`${API}/explorer/most-rigorous?limit=${limit}`);
    return response.data;
  },

  getPublicArticleDetail: async (articleId) => {
    const response = await axios.get(`${API}/explorer/article/${articleId}`);
    return response.data;
  },

  getRelatedArticles: async (articleId, limit = 5) => {
    const response = await axios.get(`${API}/explorer/article/${articleId}/related?limit=${limit}`);
    return response.data;
  },

  // Topics
  createTopic: async (topic) => {
    const response = await axios.post(`${API}/topics`, topic);
    return response.data;
  },

  getTopics: async () => {
    const response = await axios.get(`${API}/topics`);
    return response.data;
  },

  linkArticleTopic: async (articleId, topicId) => {
    const response = await axios.post(`${API}/articles/${articleId}/topics/${topicId}`);
    return response.data;
  },

  // Research Analysis
  analyzeArticle: async (articleId) => {
    const response = await axios.post(`${API}/admin/article/${articleId}/analyze`);
    return response.data;
  },

  reanalyzeArticle: async (articleId) => {
    const response = await axios.post(`${API}/admin/article/${articleId}/analyze?force=true`);
    return response.data;
  },

  getResearchInsight: async (articleId) => {
    const response = await axios.get(`${API}/admin/article/${articleId}/insight`);
    return response.data;
  },

  // System
  getStats: async () => {
    const response = await axios.get(`${API}/stats`);
    return response.data;
  },

  // ========== AUTHOR AUTHENTICATION ==========
  
  registerAuthor: async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    return response.data;
  },

  loginAuthor: async (credentials) => {
    const response = await axios.post(`${API}/auth/login`, credentials);
    return response.data;
  },

  
  // ========== ARTICLE CLAIMING ==========
  
  getPossibleArticles: async (userId, threshold = 75) => {
    const response = await axios.get(`${API}/authors/possible-articles?user_id=${userId}&threshold=${threshold}`);
    return response.data;
  },

  claimArticle: async (userId, articleId) => {
    const response = await axios.post(`${API}/authors/claim-article?user_id=${userId}&article_id=${articleId}`);
    return response.data;
  },

  getMyArticles: async (userId) => {
    const response = await axios.get(`${API}/authors/my-articles?user_id=${userId}`);
    return response.data;
  },

  unclaimArticle: async (userId, articleId) => {
    const response = await axios.delete(`${API}/authors/unclaim-article?user_id=${userId}&article_id=${articleId}`);
    return response.data;
  },

  getAuthorMetrics: async (userId) => {
    const response = await axios.get(`${API}/authors/metrics?user_id=${userId}`);
    return response.data;
  },

  // ========== ARTICLE COMPARISON ==========
  compareArticles: async (ids) => {
    const response = await axios.get(`${API}/compare?ids=${ids.join(',')}`);
    return response.data;
  },

  // ========== GRAPHICAL ABSTRACT ==========
  getGraphicalAbstractData: async (articleId) => {
    const response = await axios.get(`${API}/graphical-abstract/${articleId}`);
    return response.data;
  },

  // ========== NOTIFICATIONS ==========
  getNotifications: async (userId, limit = 50) => {
    const response = await axios.get(`${API}/notifications?user_id=${userId}&limit=${limit}`);
    return response.data;
  },

  getUnreadCount: async (userId) => {
    const response = await axios.get(`${API}/notifications/unread-count?user_id=${userId}`);
    return response.data;
  },

  markNotificationRead: async (notificationId) => {
    const response = await axios.put(`${API}/notifications/${notificationId}/read`);
    return response.data;
  },

  markAllNotificationsRead: async (userId) => {
    const response = await axios.put(`${API}/notifications/read-all?user_id=${userId}`);
    return response.data;
  },

  testCitationAlert: async (userId) => {
    const response = await axios.post(`${API}/notifications/test-citation-alert?user_id=${userId}`);
    return response.data;
  }
};
